import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  Tooltip,
  IconButton,
  Checkbox,
  Button,
  Chip,
  Badge,
  Stack,
  Divider,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import FlagIcon from "@mui/icons-material/Flag";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import { GET_AUDIT_FRAMEWORK_MIDDLE_PANEL, UPDATE_AUDIT_CONTROL_STATUS } from "../../../../../apis/settingsApis/audit-apis/auditApis";
import { EXPORT_CONTROLS_EVIDENCE } from "../../../../../apis/organization-apis/evidence-apis/evidenceApis";
import SearchBar from "../../../../../components/SearchBar/SearchBar";
import { AdminStatusType, AuditorStatusType, AuditStatus } from "../../../../../utils/enums";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import ReadyForAuditDialog from "../Dialogs/ReadyForAuditDialog";
import StatusFilterPopover from "../Filters/StatusFilterPopover";
import ControlNameFilterPopover from "../Filters/ControlNameFilterPopover";

const ControlsTab = ({ audit, isAuditorView = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [allControls, setAllControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [selectedControls, setSelectedControls] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domains, setDomains] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [redFlagFilter, setRedFlagFilter] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data: frameworkData, loading, refetch } = useQuery(GET_AUDIT_FRAMEWORK_MIDDLE_PANEL, {
    variables: {
      audit_id: audit?.id,
    },
    skip: !audit?.id || !audit?.audit_start_date,
    fetchPolicy: "cache-and-network",
  });

  const [updateControlStatus] = useMutation(UPDATE_AUDIT_CONTROL_STATUS);
  const [exportControlsEvidence] = useMutation(EXPORT_CONTROLS_EVIDENCE);

  const isMarkReadyEligible = (control) =>
    isAuditorView
      ? control.status !== AuditorStatusType.APPROVED &&
        control.status !== AdminStatusType.NOT_READY
      : control.status !== AdminStatusType.READY_FOR_AUDIT &&
        control.status !== AuditorStatusType.APPROVED;

  // Evidence can only be exported once a control has reached the auditor -
  // either marked ready for audit, or already approved. Selection stays open
  // (Mark Ready needs the other rows); the export filters on the way out and
  // reports what it skipped.
  const isExportable = (control) =>
    control.status === AdminStatusType.READY_FOR_AUDIT ||
    control.status === AuditorStatusType.APPROVED;

  const selectedControlRows = filteredControls.filter((c) =>
    selectedControls.includes(c.auditControlId)
  );
  const markReadySelection = selectedControlRows
    .filter(isMarkReadyEligible)
    .map((c) => c.auditControlId);
  // What Export will actually send - the button counts and enables off this,
  // so it never offers to export controls the server would reject.
  const exportSelection = selectedControlRows.filter(isExportable);

  // Selection handlers
  const handleControlSelection = (controlId) => {
    setSelectedControls((prev) => {
      if (prev.includes(controlId)) {
        return prev.filter((id) => id !== controlId);
      } else {
        return [...prev, controlId];
      }
    });
  };

  const handleSelectAll = () => {
    if (
      selectedControls.length === filteredControls.length &&
      filteredControls.length > 0
    ) {
      setSelectedControls([]);
    } else {
      setSelectedControls(filteredControls.map((c) => c.auditControlId));
    }
  };

  // Open confirmation dialog
  const handleBulkMarkReady = () => {
    setConfirmDialogOpen(true);
  };

  const handleBulkExport = async () => {
    const skippedCount = selectedControlRows.length - exportSelection.length;

    // Export keys off controlsId, not the auditControlId the selection holds.
    const controlIds = exportSelection
      .map((c) => Number(c.controlsId))
      .filter((id) => !isNaN(id));

    if (controlIds.length === 0) {
      setSnackbarInfo({
        message:
          "None of the selected controls are ready for audit. Only controls marked ready for audit or approved can be exported.",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    setIsExporting(true);
    try {
      // The server builds the ZIP in the background and emails the link, so
      // this resolves as soon as the export is accepted - there is no file to
      // wait for here.
      const result = await exportControlsEvidence({
        variables: {
          controlIds,
          auditId: audit?.id,
          end_time: audit?.audit_end_date,
        },
      });

      const response = result?.data?.exportControlsEvidence;
      if (response?.success) {
        setSelectedControls([]);
      }

      const baseMessage =
        response?.message ||
        (response?.success
          ? "Export started. We'll email you the download link when it's ready."
          : "Could not start the export. Please try again.");

      setSnackbarInfo({
        message:
          response?.success && skippedCount > 0
            ? `${baseMessage} ${skippedCount} control(s) were skipped because they are not ready for audit.`
            : baseMessage,
        vertical: "top",
        horizontal: "right",
      });
    } catch (error) {
      console.error("Error exporting control evidence:", error);
      setSnackbarInfo({
        message: error.message || "Could not start the export. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Execute bulk mark ready/approved after confirmation
  const executeBulkMarkReady = async () => {
    setConfirmDialogOpen(false);
    setIsSubmitting(true);
    let anySuccess = false;
    let anyFailure = false;
    let firstErrorMessage = null;

    // Use different status based on role
    const targetStatus = isAuditorView ? AuditorStatusType.APPROVED : AdminStatusType.READY_FOR_AUDIT;
    const actionText = isAuditorView ? "approved" : "ready for audit";

    try {
      for (const auditControlId of markReadySelection) {
        try {
          const result = await updateControlStatus({
            variables: {
              audit_control_id: auditControlId,
              audit_id: audit?.id,
              status: targetStatus,
            },
          });

          if (result?.data?.updateAuditControlStatus?.success) {
            anySuccess = true;
          } else {
            anyFailure = true;
            const apiMsg = result?.data?.updateAuditControlStatus?.message;
            if (apiMsg && !firstErrorMessage) {
              firstErrorMessage = apiMsg;
            }
          }
        } catch (error) {
          console.error(`Failed to update control ${auditControlId}:`, error);
          anyFailure = true;
          const errMsg =
            error?.graphQLErrors?.[0]?.message ||
            error?.networkError?.result?.errors?.[0]?.message ||
            error?.message;
          if (errMsg && !firstErrorMessage) {
            firstErrorMessage = errMsg;
          }
        }
      }

      // Refetch data
      await refetch();

      // Clear selection
      setSelectedControls([]);

      const snackbarMessage = anyFailure
        ? firstErrorMessage || "Failed to update one or more controls."
        : anySuccess
          ? `Selected controls were marked as ${actionText}.`
          : "No changes were made.";

      setSnackbarInfo({
        message: snackbarMessage,
        vertical: "top",
        horizontal: "right",
      });
    } catch (error) {
      console.error("Bulk update error:", error);
      setSnackbarInfo({
        message: "Failed to update controls. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const panel = frameworkData?.getAuditFrameworkMiddlePanel;
    if (!panel?.areaDetails) return;

    const controlsMap = new Map();
    const domainsSet = new Set();

    panel.areaDetails.forEach((area) => {
      if (!area) return;

      // Extract domain from area name - break at first "-" and take the first part
      let domainValue = area.area_name || "";
      if (domainValue.includes("-")) {
        domainValue = domainValue.split("-")[0].trim();
      }

      if (domainValue) {
        domainsSet.add(domainValue);
      }

      (area?.criterias || []).forEach((criteria) => {
        (criteria?.controls || []).forEach((control) => {
          if (!control?.controlsId) return;

          const checks = control.checks || [];
          if (checks.length === 0) return; // match ControlsHome: only controls with checks

          const existing = controlsMap.get(control.controlsId);

          if (!existing) {
            controlsMap.set(control.controlsId, {
              ...control,
              controlsId: control.controlsId,
              controlsName: control.controlsName,
              controlsDescription: control.controlsDescription,
              controlsTitle: control.controlsTitle,
              auditControlId: control.auditControlId,
              status: control.status,
              redFlag: control.redFlag,
              areaName: area.area_name,
              criteriaName: criteria.criteria || "",
              domains: [area.area_name],
              domainNumbers: [domainValue],
              areaNames: [area.area_name],
              checks,
            });
          } else {
            const updated = { ...existing };

            if (!updated.domains.includes(area.area_name)) {
              updated.domains = [...updated.domains, area.area_name];
              updated.areaNames = [...updated.areaNames, area.area_name];
            }
            if (!updated.domainNumbers.includes(domainValue)) {
              updated.domainNumbers = [...updated.domainNumbers, domainValue];
            }

            controlsMap.set(control.controlsId, updated);
          }
        });
      });
    });

    const controlsArray = Array.from(controlsMap.values()).sort(
      (a, b) => (a.controlsId || 0) - (b.controlsId || 0)
    );

    setAllControls(controlsArray);
    setFilteredControls(controlsArray);
    setDomains(
      Array.from(domainsSet).sort((a, b) => {
        const numA = parseInt(a.replace("CC", ""));
        const numB = parseInt(b.replace("CC", ""));
        return numA - numB;
      })
    );
  }, [frameworkData]);

  // Filter by search query, domain, status and red flag
  useEffect(() => {
    if (!searchQuery.trim() && !selectedDomain && statusFilter.length === 0 && !redFlagFilter) {
      setFilteredControls(allControls);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allControls.filter((control) => {
      // Domain filter - check if control belongs to selected domain
      if (selectedDomain && !control.domainNumbers?.includes(selectedDomain)) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0 && control.status && !statusFilter.includes(control.status)) {
        return false;
      }

      // Red flag filter - show only red flagged controls if filter is active
      if (redFlagFilter && !control.redFlag) {
        return false;
      }

      // If no search query, we already passed domain/status/redFlag checks
      if (!query) {
        return true;
      }

      // Search in control name and description
      const controlMatch =
        (control.controlsName?.toLowerCase() || "").includes(query) ||
        (control.controlsDescription?.toLowerCase() || "").includes(query);

      // Search in checks
      const checkMatch = control.checks?.some(
        (check) =>
          (check.activeCheckName?.toLowerCase() || "").includes(query) ||
          (check.activeCheckDescription?.toLowerCase() || "").includes(query)
      );

      return controlMatch || checkMatch;
    });

    filtered.sort((a, b) => (a.controlsId || 0) - (b.controlsId || 0));
    setFilteredControls(filtered);
  }, [searchQuery, selectedDomain, statusFilter, redFlagFilter, allControls]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDomain(null);
    setStatusFilter([]);
    setRedFlagFilter(false);
  };

  const getDomainControlCount = (domain) => {
    return allControls.filter((control) =>
      control.domainNumbers?.includes(domain)
    ).length;
  };

  const columns = [
    {
      label: (
        <Checkbox
          checked={
            selectedControls.length === filteredControls.length &&
            filteredControls.length > 0
          }
          indeterminate={
            selectedControls.length > 0 &&
            selectedControls.length < filteredControls.length
          }
          onChange={handleSelectAll}
          sx={{
            p: 0,
            color: "#9CA3AF",
            "&.Mui-checked": {
              color: "#3B82F6",
            },
            "&.MuiCheckbox-indeterminate": {
              color: "#3B82F6",
            },
          }}
        />
      ),
      field: "checkbox",
      align: "center",
      width: "5%",
    },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          Control Name
          <ControlNameFilterPopover
            onFilterChange={setRedFlagFilter}
            activeFilter={redFlagFilter}
          />
        </Box>
      ),
      field: "controlsName",
      align: "left",
      width: "10%",
    },
    { label: "Control", field: "control_info", align: "left", width: "35%" },
    { label: "Domain", field: "domain", align: "left", width: "12%" },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          Status
          <StatusFilterPopover
            onFilterChange={setStatusFilter}
            activeFilters={statusFilter}
          />
        </Box>
      ),
      field: "status",
      align: "left",
      width: "14%",
    },
    { label: "", field: "actions", align: "left", width: "5%" },
  ];

  const handleControlClick = (control) => {
    const basePath = isAuditorView ? "/auditor" : "/admin";
    navigate(`${basePath}/audits/${audit.id}/control/${control.controlsId}`, {
      state: { audit },
    });
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "checkbox":
        // Every control stays selectable - a selected control that Mark Ready
        // can't act on is simply skipped by that action, and export skips
        // anything not yet ready for audit.
        const markReadySkipReason = isMarkReadyEligible(row)
          ? ""
          : isAuditorView
            ? row.status === AuditorStatusType.APPROVED
              ? "Already approved - will be skipped by Mark Approved"
              : "Control must be ready for audit before approval - will be skipped by Mark Approved"
            : row.status === AdminStatusType.READY_FOR_AUDIT
              ? "Already marked as ready for audit - will be skipped by Mark Ready"
              : "Approved by auditor - will be skipped by Mark Ready";

        return (
          <Tooltip title={markReadySkipReason} arrow>
            <span>
              <Checkbox
                checked={selectedControls.includes(row.auditControlId)}
                onChange={() => handleControlSelection(row.auditControlId)}
                sx={{
                  p: 0,
                  color: "#9CA3AF",
                  "&.Mui-checked": {
                    color: "#3B82F6",
                  },
                }}
              />
            </span>
          </Tooltip>
        );
      case "controlsName":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                color: theme.customColors.text.strong,
              }}
            >
              {row.controlsName || "-"}
            </Typography>
            {row.redFlag && (
              <Tooltip title="Red Flagged - Requires Attention" arrow placement="top">
                <Box
                  sx={{
                    position: "relative",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "#FEE2E2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.3)",
                  }}
                >
                  <FlagIcon sx={{ color: "#DC2626", fontSize: 14 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      case "control_info":
        return (
          <Box sx={{ py: 0.5 }}>
            <Tooltip
              title={row.controlsDescription || "-"}
              arrow
              placement="top"
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  width: "100%",
                  color: theme.customColors.text.strong,
                }}
              >
                {row.controlsDescription || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "actions":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                onClick={() => handleControlClick(row)}
                sx={{
                  color: "#031B89",
                  "&:hover": {
                    backgroundColor: "rgba(3, 27, 137, 0.08)",
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      case "domain":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:0.75}}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row.domainNumbers?.join(", ") || "-"}
            </Typography>
          </Box>
        );
      case "status":
        const getStatusConfig = (status) => {
          switch (status) {
            case AdminStatusType.READY_FOR_AUDIT:
              return {
                label: "Ready for Audit",
                color: theme.customColors.status.success.text,
                bgColor: theme.customColors.status.success.bg,
              };
            case AuditorStatusType.APPROVED:
              return {
                label: "Approved",
                color: theme.customColors.status.success.text,
                bgColor: theme.customColors.status.success.bg,
              };
            case AdminStatusType.NOT_READY:
              return {
                label: "Not Ready",
                color: theme.customColors.status.error.text,
                bgColor: theme.customColors.status.error.bg,
              };
            default:
              return {
                label: "Not Ready",
                color: theme.customColors.status.error.text,
                bgColor: theme.customColors.status.error.bg,
              };
          }
        };

        const statusConfig = getStatusConfig(row.status);

        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:0.75 }}>
            <Chip
              label={statusConfig.label}
              icon={statusConfig.icon}
              sx={{
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.color,
                minWidth: "90px",
                textAlign: "center",
                borderRadius: 0,
                fontWeight: 600,
                fontSize: "0.75rem",
                border: "none",
                "& .MuiChip-icon": {
                  color: statusConfig.color,
                  marginLeft: "8px",
                },
              }}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row[column.field] || "-"}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box>
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography variant="h5">
              Overview
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: "18px",
              fontWeight: "bold",
            },
            padding: "14px",
          }}
          action={
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <SearchBar
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search controls..."
                count={filteredControls.length}
                countLabel="controls found"
              />
              <Tooltip
                title={
                  selectedControls.length > 0 && exportSelection.length === 0
                    ? "None of the selected controls are ready for audit"
                    : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleBulkExport}
                    disabled={isExporting || exportSelection.length === 0}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 2.5,
                      py: 1.1,
                      whiteSpace: "nowrap",
                      borderColor: theme.customColors.button.primary,
                      color: theme.customColors.button.primary,
                      "&:hover": {
                        borderColor: theme.customColors.button.primary,
                        backgroundColor: theme.customColors.background.hover,
                      },
                      "&:disabled": {
                        borderColor: "#D1D5DB",
                        color: "#9CA3AF",
                      },
                    }}
                  >
                    {isExporting
                      ? "Starting ..."
                      : exportSelection.length > 0
                        ? `Export (${exportSelection.length})`
                        : "Export"}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  audit?.audit_status === AuditStatus.COMPLETED
                    ? `${isAuditorView ? "Mark Approved" : "Mark Ready"} is disabled because the audit has been completed`
                    : audit?.audit_status === AuditStatus.ARCHIVED
                      ? `${isAuditorView ? "Mark Approved" : "Mark Ready"} is disabled because the audit has been archived`
                      : selectedControls.length > 0 && markReadySelection.length === 0
                        ? `None of the selected controls can be marked ${isAuditorView ? "approved" : "ready"}`
                        : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleBulkMarkReady}
                    disabled={
                      isSubmitting ||
                      markReadySelection.length === 0 ||
                      audit?.audit_status === AuditStatus.COMPLETED ||
                      audit?.audit_status === AuditStatus.ARCHIVED
                    }
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 2.5,
                      py: 1.1,
                      whiteSpace: "nowrap",
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      boxShadow: "0 3px 8px rgba(0,0,0,0.18)",
                      "&:hover": {
                        bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                      },
                      "&:disabled": {
                        backgroundColor: "#D1D5DB",
                        color: "#9CA3AF",
                      },
                    }}
                  >
                    {isSubmitting
                      ? `Marking ${markReadySelection.length} ...`
                      : markReadySelection.length > 0
                        ? isAuditorView
                          ? `Mark Approved (${markReadySelection.length})`
                          : `Mark Ready (${markReadySelection.length})`
                        : isAuditorView
                          ? "Mark Approved"
                          : "Mark Ready"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          {domains.length > 0 && (
            <Box
              sx={{
                p: 3,
                pb: 2,
                backgroundColor: "#FEFEFE",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FilterListIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                  <Typography
                    variant="body1"
                    color="#374151"
                    fontWeight="600"
                  >
                    Filter by Domain
                  </Typography>
                </Box>
                {(searchQuery || selectedDomain || statusFilter.length > 0 || redFlagFilter) && (
                  <Tooltip title="Clear all filters" arrow>
                    <IconButton
                      size="small"
                      onClick={clearFilters}
                      sx={{
                        color: "#6B7280",
                        backgroundColor: "#F3F4F6",
                        "&:hover": {
                          backgroundColor: "#E5E7EB",
                          color: "#374151",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
                {domains.map((domain) => {
                  return (
                    <Badge
                      key={domain}
                      color="primary"
                      sx={
                        selectedDomain === domain
                          ? {
                              "& .MuiBadge-badge": {
                                backgroundColor: "#ffffff",
                                color: "#3B82F6",
                              },
                            }
                          : {
                              "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                minWidth: 18,
                                height: 18,
                                backgroundColor: "#3B82F6",
                                color: "#ffffff",
                              },
                            }
                      }
                    >
                      <Chip
                        label={domain}
                        onClick={() =>
                          setSelectedDomain(
                            selectedDomain === domain ? null : domain
                          )
                        }
                        variant={
                          selectedDomain === domain ? "filled" : "outlined"
                        }
                        sx={
                          selectedDomain === domain
                            ? {
                                cursor: "pointer",
                                height: "38px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                borderRadius: "12px",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: "scale(1.05)",
                                backgroundColor: "#3B82F6",
                                color: "#ffffff",
                                border: "2px solid #3B82F6",
                                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                                "&:hover": {
                                  backgroundColor: "#2563EB",
                                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
                                },
                              }
                            : {
                                cursor: "pointer",
                                height: "38px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                borderRadius: "12px",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: "scale(1)",
                                backgroundColor: "#ffffff",
                                color: "#6B7280",
                                border: "2px solid #E5E7EB",
                                "&:hover": {
                                  backgroundColor: "#F9FAFB",
                                  borderColor: "#9CA3AF",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                  transform: "scale(1.02)",
                                },
                              }
                        }
                      />
                    </Badge>
                  );
                })}
              </Stack>
            </Box>
          )}

          {domains.length > 0 && <Divider sx={{ borderColor: "#E8EAED" }} />}

          {loading ? (
            <TableSkeleton />
          ) : (
            <ReusableTable
              columns={columns}
              data={filteredControls}
              renderCell={renderCell}
              loading={loading}
              height="auto"
              showPagination={true}
              rowsPerPageOptions={[50, 100]}
            />
          )}
        </CardContent>
      </Card>

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      <ReadyForAuditDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeBulkMarkReady}
        loading={isSubmitting}
        isAuditorView={isAuditorView}
      />
    </Box>
  );
};

export default ControlsTab;
